import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import { IndustryEnum } from '@dashboard/enums/industry';

import { useAdminSellers } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import plans from '@dashboard/services/plans';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { FormElement, Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';

import Circle from '../components/Circle';
import { formatContractDate, getContractEndStatus, getRenewalStatus } from './merchantUtils';

const planHandles = plans.reduce((acc, plan) => {
  if (!acc.find(p => p.handle === plan.handle)) {
    acc.push(plan);
  }

  return acc;
}, []);

const Merchants = () => {
  const history = useHistory();

  const urlQuery = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput(urlQuery.search);
  const [sortBy, setSortBy] = useState(urlQuery.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(urlQuery.sortDirection || -1);
  const [limit] = useState(20);
  const [plan, setPlan] = useState(urlQuery.plan || '');
  const [billingPeriod, setBillingPeriod] = useState(urlQuery.billingPeriod || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState(urlQuery.subscriptionStatus || '');
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] = useState(
    urlQuery.stripeSubscriptionStatus || '',
  );

  const [industry, setIndustry] = useState(urlQuery.industry || '');
  const [hasCustomPricing, setHasCustomPricing] = useState(urlQuery.hasCustomPricing || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(plan && { plan }),
        ...(billingPeriod && { billingPeriod }),
        ...(subscriptionStatus && { subscriptionStatus }),
        ...(stripeSubscriptionStatus && { stripeSubscriptionStatus }),
        ...(industry && { industry }),
        ...(hasCustomPricing && { hasCustomPricing }),
        ...(search && { search }),
      }),
    });
  }, [
    sortBy,
    sortDirection,
    plan,
    subscriptionStatus,
    stripeSubscriptionStatus,
    industry,
    hasCustomPricing,
    search,
    billingPeriod,
  ]);

  const filters = {
    ...(plan && {
      'plan.handle': plan,
    }),
    ...(billingPeriod && {
      'plan.billingPeriod': billingPeriod,
    }),
    ...(subscriptionStatus && {
      subscriptionStatus,
    }),
    ...(stripeSubscriptionStatus && {
      stripeSubscriptionStatus,
    }),
    ...(industry && { industry }),
    ...(hasCustomPricing === 'true' && {
      'plan.volumeLimit': JSON.stringify({ $exists: true, $ne: null }),
    }),
    ...(hasCustomPricing === 'false' && {
      $or: JSON.stringify([
        { 'plan.volumeLimit': { $exists: false } },
        { 'plan.volumeLimit': null },
      ]),
    }),
    search,
    limit,
    sort: JSON.stringify({
      [sortBy]: sortDirection,
    }),
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useAdminSellers(
    filters,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'displayName',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Store" />,
        cell: ({ row }) => {
          const seller = row.original;
          return (
            <div className="relative max-w-xs">
              <Link to={`/admin/merchants/${seller._id}`}>
                {seller.displayName || seller.name || 'N/A'}
              </Link>
              {seller.disabled && (
                <Badge variant="secondary" className="ml-2 absolute right-0 mt-0.5">
                  Disabled
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'industry',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Store focus" />,
        cell: ({ row }) => {
          const seller = row.original;
          return seller.industry ? upperFirst(seller.industry.replace(/-/g, ' ')) : 'N/A';
        },
        enableSorting: true,
      },
      {
        id: 'plan.handle',
        accessorKey: 'plan.handle',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Plan" />,
        cell: ({ row }) => {
          const seller = row.original;
          return (
            <div>
              {seller.plan?.title}
              {seller.plan?.handle === 'scale' && seller.plan?.bracket && (
                <span> {seller.plan.bracket / 1000}K</span>
              )}
              {seller.plan?.billingPeriod && <span> - {seller.plan?.billingPeriod}</span>}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'subscriptionStatus',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const seller = row.original;
          return <div>{getRenewalStatus(seller, true)}</div>;
        },
        enableSorting: true,
      },
      {
        id: 'connected',
        header: 'Connected',
        cell: ({ row }) => {
          const seller = row.original;
          return (
            <div>
              <Circle color={checkIsConnectedToStripe(seller) ? 'green' : 'red'} />
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'subscribed',
        header: 'Subscribed',
        cell: ({ row }) => {
          const seller = row.original;
          return (
            <div>
              <Circle color={seller.stripeSubscriptionId ? 'green' : 'red'} />
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'checkouts',
        header: 'Checkouts',
        cell: ({ row }) => {
          const seller = row.original;
          return <div>{seller.pages}</div>;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'amountOfUsers',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Team members" />,
        cell: ({ row }) => {
          const seller = row.original;
          return <div>{seller.amountOfUsers}</div>;
        },
        enableSorting: true,
      },
      ...(hasCustomPricing === 'true'
        ? [
            {
              id: 'plan.contractEndDate',
              accessorKey: 'plan.contractEndDate',
              header: ({ column }) => (
                <DataGridColumnHeader column={column} title="Contract ends" />
              ),
              cell: ({ row }) => {
                const contractEndDate = row.original.plan?.contractEndDate;
                const status = getContractEndStatus(contractEndDate);
                return (
                  <div className="whitespace-nowrap">
                    {contractEndDate ? formatContractDate(contractEndDate) : ''}
                    {status && (
                      <Badge variant={status.variant} className="ml-2">
                        {status.label}
                      </Badge>
                    )}
                  </div>
                );
              },
              enableSorting: true,
            },
          ]
        : []),
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Created at" />,
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'd MMM, yyyy, HH:mm') : '';
        },
        enableSorting: true,
      },
    ],
    [hasCustomPricing],
  );

  const total = data?.pages[0]?.total;

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto text-sm text-gray-500 dark:text-muted-foreground whitespace-nowrap">
        {typeof total === 'number' &&
          `${total.toLocaleString()} ${total === 1 ? 'merchant' : 'merchants'}`}
      </div>

      <FormElement className="!mb-0">
        <Label htmlFor="industry">Store focus</Label>
        <Select
          name="industry"
          value={industry || 'all'}
          onValueChange={value => setIndustry(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {Object.values(IndustryEnum).map(currentIndustry => (
              <SelectItem key={currentIndustry} value={currentIndustry}>
                {upperFirst(currentIndustry)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="plan">Plan</Label>
        <Select
          name="plan"
          value={plan || 'all'}
          onValueChange={value => {
            setPlan(value === 'all' ? '' : value);
            if (!subscriptionStatus) {
              setSubscriptionStatus('active');
            }
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {planHandles.map(planHandle => (
              <SelectItem key={planHandle.handle} value={planHandle.handle}>
                {planHandle.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="billingPeriod">Billing period</Label>
        <Select
          name="billingPeriod"
          value={billingPeriod || 'all'}
          onValueChange={value => {
            setBillingPeriod(value === 'all' ? '' : value);
            if (!subscriptionStatus) {
              setSubscriptionStatus('active');
            }
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="year">Year</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="subscriptionStatus">Subscription status</Label>
        <Select
          name="subscriptionStatus"
          value={subscriptionStatus || 'all'}
          onValueChange={value => setSubscriptionStatus(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="trial_expired">Trial expired</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="legacy">Legacy</SelectItem>
            <SelectItem value="comped">Comped</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="stripeSubscriptionStatus">Stripe renewal status</Label>
        <Select
          name="stripeSubscriptionStatus"
          value={stripeSubscriptionStatus || 'all'}
          onValueChange={value => setStripeSubscriptionStatus(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="past_due">Past due</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="incomplete">Incomplete</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="hasCustomPricing">Custom pricing</Label>
        <Select
          name="hasCustomPricing"
          value={hasCustomPricing || 'all'}
          onValueChange={value => setHasCustomPricing(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name or ID"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </FormElement>
    </div>
  );

  return (
    <React.Fragment>
      <div className="mb-4">{filterControls}</div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No stores found</EmptyState.Title>
          </EmptyState>
        }
      />
    </React.Fragment>
  );
};

export default Merchants;
